/** @jsx jsx */
import { jsx } from '@emotion/core'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { LiveProvider, LiveEditor, LiveError, LivePreview } from 'react-live'
import Highlight, { defaultProps } from 'prism-react-renderer'
import { transform } from '@babel/standalone'
import { preToCodeBlock } from 'mdx-utils'
import { withPrefix } from 'gatsby'
import {
  Box,
  Button,
  Card,
  Divider,
  Heading,
  Icon,
  Link,
  StackView,
  Text,
  hooks,
} from '@planning-center/ui-kit'

import { EDITOR_SCOPE } from './editor-scope'
import { EDITOR_THEME } from './editor-theme'

const RE = /{([\d,-]+)}/

function calculateLinesToHighlight(meta) {
  if (RE.test(meta)) {
    const lineNumbers = RE.exec(meta)[1]
      .split(',')
      .map((v) => v.split('-').map((y) => parseInt(y, 10)))
    return (index) => {
      const lineNumber = index + 1
      const inRange = lineNumbers.some(([start, end]) =>
        end ? lineNumber >= start && lineNumber <= end : lineNumber === start
      )
      return inRange
    }
  } else {
    return () => false
  }
}

function LiveCode({ codeString }) {
  const editorRef = useRef(null)
  const codeRef = useRef(codeString)
  const [on, setOn] = useState(false)
  const [justCopied, setJustCopied] = useState(false)
  const transformCode = useCallback((input) => {
    try {
      const { code } = transform(input, { presets: ['stage-3', 'react'] })
      codeRef.current = input
      return code
    } catch (err) {
      return input
    }
  }, [])
  useEffect(() => {
    if (on) {
      const textarea = editorRef.current.querySelector('textarea')
      if (textarea?.focus) {
        textarea.focus()
      }
    }
  }, [on])
  return (
    <LiveProvider
      code={codeString}
      scope={EDITOR_SCOPE}
      theme={EDITOR_THEME}
      noInline={true}
      transformCode={transformCode}
    >
      <Card
        overflow="auto"
        marginVertical={4}
        radius={8}
        position="relative"
        data-live-code=""
      >
        <StackView position="relative">
          <LivePreview
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 40,
              backgroundColor: 'white',
            }}
          />
          <StackView
            axis="horizontal"
            spacing={1}
            position="absolute"
            bottom={1}
            right={1}
          >
            <Button
              size="xs"
              title={justCopied ? 'Copied 🎉' : 'Copy'}
              onClick={() => {
                navigator.clipboard.writeText(codeRef.current)
                setJustCopied(true)
                setTimeout(() => {
                  setJustCopied(false)
                }, 1000)
              }}
            />
            <Button
              size="xs"
              title={on ? 'Done' : 'Edit'}
              radius={0}
              onClick={() => setOn((bool) => !bool)}
            />
          </StackView>
        </StackView>
        <StackView innerRef={editorRef} visible={on}>
          <LiveEditor
            padding={16}
            css={{
              backgroundColor: EDITOR_THEME.plain.backgroundColor,
              fontFamily: `Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace`,
              fontSize: '1em',
              ':focus-within': {
                backgroundColor: 'hsl(200,8%,12%)',
              },
              '> textarea, > pre': {
                gridColumn: '2/3',
                outline: 0,
                ':hover': {
                  backgroundColor: 'hsl(200,8%,12%)',
                },
              },
            }}
            style={{
              fontFamily: undefined,
              padding: undefined,
              backgroundColor: undefined,
            }}
          />
        </StackView>
        <LiveError
          css={{
            padding: '1em',
            borderRadius: 0,
            backgroundColor: '#d83d3d',
            color: 'white',
          }}
        />
      </Card>
    </LiveProvider>
  )
}

function SyntaxHighligher({ codeString, language, metastring }) {
  const shouldHighlightLine = calculateLinesToHighlight(metastring)
  return (
    <Highlight
      {...defaultProps}
      code={codeString}
      language={language}
      theme={EDITOR_THEME}
    >
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <Box
          marginVertical={4}
          backgroundColor={EDITOR_THEME.plain.backgroundColor}
          radius={8}
          overflow="auto"
        >
          <Box
            as="pre"
            className={className}
            padding={2}
            margin={0}
            fontSize={4}
            lineHeight="1.4"
            fontFamily="`Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace`"
            style={style}
          >
            {tokens.map((line, index) => (
              <Box
                {...getLineProps({ line, key: index })}
                css={{
                  display: 'flex',
                  backgroundColor: shouldHighlightLine(index)
                    ? 'hsl(209, 58%, 14%)'
                    : undefined,
                }}
              >
                {line.map((token, key) => (
                  <span {...getTokenProps({ token, key })} />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Highlight>
  )
}

const headings = [1, 2, 3, 4, 5, 6].reduce((collection, level) => ({
  ...collection,
  [`h${level}`]: (props) => {
    const { hover, hoverProps } = hooks.useHover()
    return (
      <StackView
        axis="horizontal"
        alignment="center"
        marginTop={3}
        marginBottom={2}
        position="relative"
        {...hoverProps}
      >
        <Box
          as="a"
          href={`#${props.id}`}
          visible={hover}
          position="absolute"
          padding={0.25}
          x="-100%"
          mediaQueries={{
            md: { padding: 1 },
          }}
        >
          <Icon name="link" color="foregroundSecondary" />
        </Box>
        <Heading id={props.id} level={level}>
          {props.children}
        </Heading>
      </StackView>
    )
  },
}))

export const components = {
  ...headings,
  a: ({ href, ...props }) => (
    <Link to={href.includes('http') ? href : withPrefix(href)} {...props} />
  ),
  p: (props) => <Text {...props} as="p" fontSize={3} lineHeight="1.6" />,
  hr: (props) => <Divider as="hr" marginBottom={2} />,
  table: (props) => (
    <Box
      as="table"
      display="table"
      width="100%"
      textAlign="left"
      borderCollapse="separate"
      borderSpacing={0}
      css={{
        th: {
          padding: 1,
          borderBottomStyle: 'solid',
          borderBottomColor: 'grey-5',
          borderBottomWidth: 2,
        },
        td: {
          padding: 1,
          borderBottomStyle: 'solid',
          borderBottomColor: 'grey-5',
          borderBottomWidth: 1,
        },
      }}
      {...props}
    />
  ),
  ul: (props) => (
    <StackView
      {...props}
      as="ul"
      fontSize={3}
      lineHeight="1.6"
      paddingLeft={1}
      marginBottom={1}
      spacing={1}
    />
  ),
  li: (props) => (
    <Box
      {...props}
      as="li"
      display="list-item"
      fontSize={3}
      lineHeight="1.6"
      listStyle="square inside"
    />
  ),
  pre: (props) => {
    const codeProps = preToCodeBlock(props)
    if (codeProps) {
      if (props.children.props.live) {
        return <LiveCode {...codeProps} />
      } else {
        return <SyntaxHighligher {...codeProps} />
      }
    } else {
      return <pre {...props} />
    }
  },
}
